import { Component, Input, ElementRef, ViewChild, OnInit, AfterViewInit } from '@angular/core';

import { Tile } from '../models/tile';
import { TileService } from '../services/tile.service';
import { LocalizeService } from '../services/localize.service';
import { ListTileComponent, ListTilesContainerInterface } from '../lists/list-tile.component';
import { HeroTileComponent } from './hero-tile-component';
import { MenuId } from '../services/menu.service';
import { Util } from '../utils/utils.module';
import { CommandHandler } from '../models/command-handler';
import { ListItem } from '../models/list-item';
import { FormService } from '../services/form.service';
import { FavoriteService } from '../services/favorite.service';

@Component({
  selector: 'edx-tile',
  styleUrls: ['tile.component.scss'],
  template: `
    @if (desc.index >= 0) {
      <div #elRef class="tile" [edx-draggable]="this" [edx-drag-target]="this" tabindex="0" (keyup.enter)="doHdrClick($event)" (keydown.space)="doHdrClick($event)"
        [ngClass]="{hero:desc.name==='hero',oneXone:desc.size===1,oneXtwo:desc.size===2,twoXone:desc.size===3,twoXtwo:desc.size===4,dragHole:desc.name==='edx-drag-hole',phone:isPhoneLook(),ios:isiOS,oai:isAddin,officeapp:isOfficeApp,selected:selected}">
        @if (desc.name!=='hero' && desc.name!=='edx-drag-hole') {
          <div (click)="doHdrClick($event)" class="tile-header"
            [ngClass]="{folder:isFolder(),flexfolder:isFlexFolder(),workspace:isWorkspace(),search:isSearch(),recentedits:isRecentEdits(),downloads:isDownloads(),imports:isImports(),fileplans:isFilePlans(),requests:isRequests(),activities:isActivities()}">
            <img class="tile-header-icon" role="button" src="{{iconUrl}}" alt="{{this.expandText + desc.name}}" (mouseover)="showExpand($event)" (mouseleave)="hideExpand($event)" (focus)="showExpand($event)" (focusout)="hideExpand($event)" tabindex="0">
            @if (!isAddin) {
              <h1 class="tile-title" title="{{desc.tooltip}}" (mouseover)="showExpand($event)" (mouseleave)="hideExpand($event)" (focus)="showExpand($event)" (focusout)="hideExpand($event)">{{desc.name}}</h1>
            }
            @if (!isPhoneLook()) {
              <edx-menu [menuID]="tileMenuId" [callback]="this" [forceMenuButton]="!isPhoneLook()" class="menu-button" id="{{ tileId +'_actions' }}"></edx-menu>
            }
          </div>
        }
        @if (showList) {
          <div class="tile-list" [ngClass]="{dragover:(dragover && !listTile.dragFolderHilited)}">
            <edx-list-tile #listTile [desc]="desc" [tcc]="parent"></edx-list-tile>
          </div>
        }
        @if (!isPhoneLook() && desc.name==='hero') {
          <edx-hero-tile #heroTile [showDragOver]="dragover"></edx-hero-tile>
        }
      </div>
    }
    `,
  standalone: false
})

export class TileComponent implements CommandHandler, OnInit, AfterViewInit {
  public tileMenuId: MenuId = 1;
  public dragover = false;
  public didADrag = false;
  public iconUrl: string;
  public headerText: string;
  public expandText: string;
  private showList: boolean;
  private isiOS: boolean = Util.Device.bIsIOSDevice;
  private isAddin: boolean = Util.Device.bIsAddin;
  private isOfficeApp: boolean = Util.Device.bIsOfficeAddinDesktop;
  @ViewChild('elRef') elRef: ElementRef;
  @ViewChild('listTile') listTile: ListTileComponent;
  @ViewChild('heroTile') heroTile: HeroTileComponent;
  @Input() desc: Tile;
  @Input() parent: TilesContainerInterface;
  @Input() tileId: string;
  @Input() selected: boolean;
  @Input() isFirstTile: boolean;

  constructor(private tileService: TileService, private localizer: LocalizeService, private formService: FormService,
    private favoriteService: FavoriteService) {
  }

  ngOnInit(): void {
    this.isAddin = Util.Device.bIsAddin;
    this.iconUrl = Util.Transforms.tileIconUrlFromDesc(this.desc);
    this.headerText = this.localizer.getTranslation('ALT_TEXT.EXPAND');
    this.showList = this.desc && !this.isPhoneLook() && this.desc.name !== 'hero';
    this.expandText = this.localizer.getTranslation('FORMS.BUTTONS.EXPAND');
  }

  ngAfterViewInit(): void {
    if (this.isFirstTile) {
      this.getElement().focus();
    }
  }
  private isPhoneLook(): boolean {
    return Util.Device.isPhoneLook();
  }
  private isFolder(): boolean {
    return this.desc.type === 'folders' && !this.isRecentEdits() && !this.isDownloads() && !this.isImports();
  }
  private isFlexFolder(): boolean {
    return this.desc.type === 'flexfolders';
  }
  private isWorkspace(): boolean {
    return this.desc.type === 'workspaces';
  }
  private isSearch(): boolean {
    return this.desc.type === 'searches';
  }
  private isRecentEdits(): boolean {
    return this.desc.id === 'recentedits';
  }
  private isDownloads(): boolean {
    return this.desc.id === 'downloads';
  }
  private isImports(): boolean {
    return this.desc.id === 'imports';
  }
  private isFilePlans(): boolean {
    return this.desc.type === 'fileplans';
  }
  private isRequests(): boolean {
    return this.desc.type === 'requests';
  }
  private isActivities(): boolean {
    return this.desc.type === 'activities';
  }

  private showExpand(event: MouseEvent): void {
    if (!Util.Device.isMobile()) {
      this.iconUrl = 'assets/images/maximize.svg';
    }
  }

  private hideExpand(event: MouseEvent): void {
    this.iconUrl = Util.Transforms.tileIconUrlFromDesc(this.desc);
    this.headerText = this.localizer.getTranslation('ALT_TEXT.EXPAND', [this.desc.tooltip]);
  }

  public refresh(): void {
    if (this.listTile) {
      this.listTile.reloadList();
    } else if (this.heroTile) {
      this.heroTile.reloadList();
    }
  }

  doHdrClick(event: Event) {
    if (!!event && event instanceof KeyboardEvent) {
      event.preventDefault();
    }
    if (!this.didADrag) {
      if (this.desc.name !== 'hero') {
        setTimeout(() => {
          this.tileService.openTile(this.desc);
        }, 500);
        if (this.parent) {
          this.parent.tileOpened(this);
        }
      }
    }
    this.didADrag = false;
  }
  doChangeSize() {
    this.desc.size = this.desc.size === 1 ? 2 : 1;
    event.stopPropagation();
  }
  doHideTile() {
    this.tileService.hideTile(this.desc);
    return false;
  }
  canDrag(x: number, y: number, element: ElementRef): boolean {
    if (!Util.Device.bIsTouchDevice && (this.desc.name === 'hero' || (x > 40 && x < (element.nativeElement.offsetWidth - 40) && y < 60))) {
      return true;
    }
    return false;
  }
  dragStarted(element: ElementRef) {
    if (this.parent) {
      this.parent.tileDragStarted(this, element);
    }
  }
  dragMoved(x: number, y: number, element: ElementRef) {
    if (this.parent) {
      this.parent.tileDragMoved(this, x, y, element);
    }
  }
  dragEnded(event: any, element: ElementRef) {
    if (this.parent) {
      this.parent.tileDragEnded(this, event, element);
    }
    this.didADrag = true;
    //stupid kludge to get around a chrome bug that will always open the last dragged tile
    setTimeout(() => {
      this.didADrag = false;
    }, 250);
  }
  acceptDrag(event: any, element: ElementRef): boolean {
    const isFileDropTarget: boolean = Util.isUploadTarget(this.desc, this.listTile?.getSet());
    if (this.heroTile || (isFileDropTarget && (!this.listTile || this.listTile.getSet().readonly !== 'Y'))) {
      if (Util.dragHasFiles(event.dataTransfer)) {
        this.dragover = true;
        return true;
      } else if (!this.heroTile && isFileDropTarget && Util.dragHasJSON(event.dataTransfer)) {
        // No external files but we allow drag and drop of DM documents into folders
        this.dragover = true;
        return true;
      }
    }
    return false;
  }
  dragOver(event: any, element: ElementRef): boolean {
    const overFolder: ListItem = this.listTile ? this.listTile.overDragFolder() : null;
    const intoThisFolder: any = overFolder || this.desc;
    let isFileDropTarget: boolean = Util.isUploadTarget(intoThisFolder, this.listTile?.getSet());
    const isCreateRefTarget: boolean = Util.isSaveToLocation(intoThisFolder) || Util.isExternalSaveToLocation(intoThisFolder) || Util.RestAPI.isTeamsAppUploadTarget(this.desc, this.listTile?.getSet()) || Util.Transforms.isFavoriteFolder(intoThisFolder.id);
    const dragDesc = Util.RestAPI.getDragDesc();
    if (!overFolder) {
      if (Util.dragHasFiles(event.dataTransfer)) {
        if (this.dragover && !this.heroTile) {
          Util.RestAPI.showDropBanner(this.desc.name);
        } else {
          if (Util.RestAPI.dropContainsFolders(event?.dataTransfer)) {
            Util.RestAPI.showDropBanner(null);
          } else {
            Util.RestAPI.showDropBanner('TILE_NAMES.RECENTLY_EDITED');
          }
        }
      } else if ((isCreateRefTarget || this.desc.id === 'recentedits') && Util.dragHasJSON(event.dataTransfer) && !(Util.isExternalLib(dragDesc.lib) && Util.Transforms.isFavoriteFolder(this.desc.id))) {
        if (isCreateRefTarget) {
          Util.RestAPI.showDropBanner(this.desc.name, 'HEADER.DRAG_CREATE_REF');
        } else {
          Util.RestAPI.showDropBanner('TILE_NAMES.RECENTLY_EDITED');
        }
      } else {
        Util.RestAPI.showDropBanner(null);
        isFileDropTarget = false;
      }
    }
    return isFileDropTarget || isCreateRefTarget;
  }
  dragLeave(event: any, element: ElementRef): void {
    this.dragover = false;
  }
  drop(event: any, element: ElementRef): void {
    const overFolder: ListItem = this.listTile ? this.listTile.overDragFolder() : null;
    let intoThisFolder: any = overFolder || this.desc;
    const isReadOnly: any = this.listTile ? this.listTile.getSet().readonly === 'Y' : false;
    const isFileDropTarget: boolean = !!this.heroTile || (Util.isUploadTarget(intoThisFolder, this.listTile.getSet()) && !isReadOnly);
    const isCreateRefTarget: boolean = Util.isSaveToLocation(intoThisFolder) || Util.isExternalSaveToLocation(overFolder || this.desc) || Util.Transforms.isFavoriteFolder(intoThisFolder.id);
    const hasFiles: boolean = Util.dragHasFiles(event.dataTransfer);
    const items: DataTransferItem[] = event.dataTransfer && event.dataTransfer.items ? event.dataTransfer && event.dataTransfer.items : null;
    const doDrop = () => {
      Util.getDropFiles(event.dataTransfer, intoThisFolder).then((fileItems: any[]) => {
        if (((fileItems && fileItems.length) || this.isAddin) && this.listTile?.desc['PD_ACTIVE_STATUS'] !== 'C') {
          const fileList = Util.RestAPI.removeZeroSizeFiles(fileItems);
          const countEmptyFiles = fileItems.length - fileList.length;
          if (this.listTile) {
            if (!this.listTile.overDragFolder() && countEmptyFiles < fileItems.length) {
              this.listTile.uploadFiles(fileList);
            }
          } else if (this.heroTile || this.isAddin) {
            intoThisFolder = this.heroTile ? { type: 'folders', id: 'recentedits', lib: '' } : this.desc;
            //const files: File[] = (!!fileList && fileList.length && typeof fileList[0] !== 'string') ? fileList : null;
            const filePaths: string[] = (!!fileList && fileList.length && typeof fileList[0] === 'string') ? fileList : null;
            if (countEmptyFiles < fileItems.length) {
              Util.RestAPI.uploadFilesWithUI(fileList, intoThisFolder, null, filePaths);
            }
          }
        }
      });
    };
    if (isFileDropTarget) {
      if (hasFiles && !Util.Transforms.isFavoriteFolder(this.desc.id) && !Util.RestAPI.isFolderDropDone()) {
        doDrop();
      } else if ((isCreateRefTarget || Util.dragHasPromisedFile(event.dataTransfer)) && Util.dragHasJSON(event.dataTransfer)) { // No external files but we allow drag and drop of DM documents into folders
        const name: string = intoThisFolder.name || intoThisFolder['DOCNAME'];
        let notifyTitle: string = this.localizer.getTranslation('FOLDER_ACTIONS.ADDED_TO_SINGLE', [name]);
        let jsonItem: DataTransferItem;
        if (!!items) {
          for (const item of items) {
            if (item.kind === 'string' && item.type === 'text/json') {
              jsonItem = item;
              break;
            }
          }
        }
        jsonItem.getAsString(itemJson => {
          const item: any = itemJson ? JSON.parse(itemJson) : null;
          if (item) {
            if (Util.isExternalLib(item.lib) && !Util.isExternalLib(intoThisFolder['lib'])) {
              if (!Util.Transforms.isFavoriteFolder(intoThisFolder['id'])) {
                if (!!item['checkout']) {
                  const listItem: ListItem = new ListItem(item);
                  Util.RestAPI.setCurList([listItem], null);
                  Util.RestAPI.getAppComponent().doCommand('checkin');
                } else {
                  // Drag and drop of an original external file being saved to eDocs
                  const dataFiles = [{ name: item['DOCNAME'], url: item['url'] || item['locationUrl'], externalName: item['DOCNAME'], size: Number(item.size) }];
                  Util.RestAPI.uploadFilesWithUI(null, null, dataFiles, intoThisFolder);
                }
              }
            } else if (Util.isExternalLib(this.desc.lib) && !Util.isExternalLib(item.lib)) {
              const checkoutItem: ListItem = new ListItem(item);
              if (item['STATUS'] !== '3') {
                //
                //This will pop up the checkout form with location chooser
                //Util.RestAPI.setCurList([checkoutItem],null);
                //Util.RestAPI.getAppComponent().doCommand('checkout');
                //
                // The following wll just prompt for confirmation to checkout to selected external location/folder
                const kind = 'profile';
                this.formService.getFormData(intoThisFolder, kind, null).then((formData: any): void => {
                  const ref: any = formData;
                  if (!!ref) {
                    const queryargs = null;
                    const params = 'profile';
                    const sendRaw = false;
                    notifyTitle = this.localizer.getTranslation('DOC_STATUS.CHECKEDOUT');
                    const notifyBody: string = checkoutItem['DOCNAME'];
                    const serverData: any = {};
                    serverData['$edx_folder_loc'] = intoThisFolder['id'];
                    serverData['%STATUS'] = '%LOCK_FOR_CHECKOUT';
                    serverData._restapi = {};
                    serverData._restapi['ref'] = ref;
                    const version = !!checkoutItem['VERSION_ID'] ? checkoutItem['VERSION_ID'] : 'C';
                    serverData._restapi['src'] = {
                      ver: version,
                      id: checkoutItem.id,
                      type: checkoutItem.type,
                      lib: checkoutItem.lib
                    };
                    const title = this.localizer.getTranslation('FOLDER_ACTIONS.CHECK_OUT');
                    const docName = ref['DOCNAME'] || intoThisFolder['DOCNAME'] || intoThisFolder['name'];
                    const body = this.localizer.getTranslation('FOLDER_ACTIONS.CHECK_OUT_TO', [checkoutItem['DOCNAME'], docName]);
                    Util.Notify.confirm(title, body, null, null, true, true, true).then(confirmed => {
                      if (confirmed && confirmed.confirm) {
                        this.formService.putToServer(checkoutItem, serverData, params, queryargs, sendRaw, notifyTitle, notifyBody);
                      }
                    });
                  }
                }).catch(error_code => {
                  Util.Notify.error(this.localizer.getTranslation('GENERIC_ERRORS.ERROR'), this.localizer.getTranslation('RAPI_ERRORS.' + error_code));
                });
              }
            } else if (!Util.isExternalLib(item.lib) && !Util.isExternalLib(intoThisFolder['lib'])) {
              Util.RestAPI.post(item, intoThisFolder, 'references').toPromise().then((response) => {
                if (Util.Transforms.isFavoriteFolder(intoThisFolder['id']) && !!response && !!response['list'] && response['list'].length > 0) {
                  this.favoriteService.handleAddToFavoriteResponse(response['list'][0], item);
                }
                if (this.listTile) {
                  this.listTile.reloadList();
                }
                Util.Notify.success(notifyTitle);
              }).catch(error => {
                Util.Notify.warning(this.localizer.getTranslation('RAPI_ERRORS.0'), error);
              });
            } else if (Util.isExternalLib(item.lib) && Util.isExternalLib(intoThisFolder['lib']) && !(item.lib === intoThisFolder['lib'] && item['parentId'] === intoThisFolder['id'])) {
              if (!item['checkout']) {
                this.formService.getFormData(intoThisFolder, 'profile', null).then((targetFolderData: any): void => {
                  const serverData: any = {};
                  serverData['LOCATION'] = item['locationUrl'];
                  serverData._restapi = {};
                  serverData._restapi['items'] = [];
                  const sourceItem = {
                    DOCNAME: item['DOCNAME'],
                    LOCATION: item['locationUrl'],
                    lib: item['lib']
                  };
                  serverData._restapi['items'].push(sourceItem);
                  item['name'] = item['DOCNAME'];
                  const files: any[] = [];
                  files.push(item);
                  Util.RestAPI.uploadFilesWithUI(files, null, null, targetFolderData, null, serverData);
                });
              }
            }
          }
        });
      }
    } else if (hasFiles) {
      // drop in recent edits
      doDrop();
    }
    this.dragover = false;
    this.parent.dropHandled();
  }

  // menu interface methods
  doCommand(cmd: string): boolean {
    const handled = false;

    switch (cmd) {
      case 'remove':
        this.doHideTile();
        break;
      case 'large':
      case 'small':
        if ((cmd === 'large' && this.desc.size !== 2) || (cmd === 'small' && this.desc.size !== 1)) {
          this.desc.size = cmd === 'large' ? 2 : 1;
          this.parent.tileChangedSize(this);
          const savedDesc: Tile = this.desc;
          this.desc = new Tile(this.desc);
          setTimeout(() => {
            // force the change detector to run by giving the list a new desc as just changing a field will not run the change detector
            this.desc = savedDesc;
          }, 1);
        }
        break;
      case 'rename': {
        const title: string = this.localizer.getTranslation('TILE_MENU.RENAME');
        const okBtn: string = this.localizer.getTranslation('FOLDER_ACTIONS.RENAME');
        const formData: any = { DOCNAME: this.desc.name };
        Util.Notify.info(title, '__local_rename', okBtn, formData, true, true, true).then(confirmed => {
          if (confirmed && confirmed.confirm) {
            const name: string = confirmed.data.DOCNAME;
            if (name && name.length) {
              Util.Notify.success(this.localizer.getTranslation('FOLDER_ACTIONS.RENAMED_SINGLE', [this.desc.name, name]));
              this.desc.name = name;
              this.tileService.tilesChanged();
            }
          }
        });
      }
    }
    return handled;
  }

  commandEnabled(cmd: string): boolean {
    let enabled = false;
    if (!Util.RestAPI.offline()) {
      switch (cmd) {
        case 'remove':
          enabled = !!this.desc?.['UserOverridable'];
          break;
        case 'rename':
          enabled = this.desc.id !== 'recentedits' &&
            !Util.Transforms.isFavoriteFolder(this.desc.id) &&
            this.desc.id !== 'checkedout' &&
            (this.desc.type !== 'flexfolders' || this.desc.id.length) &&
            (this.desc.type !== 'searches' || this.desc.id.length) &&
            (this.desc.type !== 'workspaces' || this.desc.id.length) &&
            (this.desc.type !== 'folders' || this.desc.id !== 'public');
          break;
        case 'large':
          enabled = (this.desc.size === 1);
          break;
        case 'small':
          enabled = (this.desc.size === 2);
          break;
      }
      return enabled;
    }
  }

  public listItemChanged(data: any): void {
    if (this.listTile) {
      this.listTile.listItemChanged(data);
    } else if (this.heroTile) {
      this.heroTile.reloadList();
    }
  }

  public getElement(): HTMLElement {
    return this.elRef ? this.elRef.nativeElement : null;
  }
}

export interface TilesContainerInterface extends ListTilesContainerInterface {
  tileDragStarted(tile: TileComponent, element: ElementRef): void;
  tileDragMoved(tile: TileComponent, x: number, y: number, element: ElementRef): void;
  tileDragEnded(tile: TileComponent, event: any, element: ElementRef): void;
  tileChangedSize(tile: TileComponent): void;
  tileOpened(tile: TileComponent);
}
